import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Archive, FileCheck } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { gbp, STATUS_LABELS, catOf } from '../lib/shop';
import { PageHead, Modal, Field, Sel, Tgl, ImagePicker, FilePicker, inp } from './components';

const CATS = ['metabolic', 'recovery', 'skin', 'energy', 'cognitive', 'gh', 'specialty']
  .map((k) => [k, catOf(k).name]);
const FORMATS = ['Vial', 'Pen', 'Spray', 'Prepared Solution', 'Bundle'];
const STATUSES = Object.entries(STATUS_LABELS);

const EMPTY = {
  name: '', alternate_name: '', sku: '', slug: '', category: 'metabolic', format: 'Vial',
  strength: '', variant: '', short_description: '', long_description: '', research_information: '',
  specification: '', composition: '', storage_information: '', retail_price: 0, compare_at_price: '',
  sale_price: '', sale_start: '', sale_end: '', stock_quantity: 0, low_stock_threshold: 5,
  track_stock: true, status: 'available', featured: false, popular: false, new_arrival: false,
  badge: '', primary_image: '', gallery_images: [], batch_number: '', coa_available: false,
  coa_document: '', sds_document: '', seo_title: '', seo_description: '', active: true,
  variants: [], research_overview: null, admin_notes: '',
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('core');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/products', { params: q ? { q } : {} });
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [q]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => { setForm({ ...EMPTY }); setTab('core'); setEditing({}); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setTab('core'); setEditing(p); };

  const save = async () => {
    if (!form.name || !form.strength) { toast.error('Name and strength are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ['retail_price', 'stock_quantity', 'low_stock_threshold'].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
      ['compare_at_price', 'sale_price'].forEach((k) => { payload[k] = payload[k] === '' || payload[k] === null ? null : Number(payload[k]); });
      if (editing?.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post('/admin/products', payload);
      toast.success(editing?.id ? 'Product updated — live across catalogue, search and filters' : 'Product created — live across catalogue, search and filters');
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name} ${p.strength}"? It disappears from the storefront (orders are kept).`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success('Product archived');
    load();
  };

  const TABS = [['core', 'Core'], ['content', 'Content'], ['variants', 'Variants'], ['pricing', 'Pricing & Stock'], ['media', 'Media'], ['docs', 'COA & Batch'], ['research', 'Research Overview'], ['seo', 'SEO']];

  return (
    <div data-testid="admin-products">
      <PageHead title="Products" sub="Create, edit and manage the full catalogue. Changes go live immediately.">
        <button data-testid="add-product-button" onClick={openNew} className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#E54D2E] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Product
        </button>
      </PageHead>

      <div className="flex items-center gap-2 border hairline bg-white px-3 py-2.5 mb-4 max-w-sm">
        <Search className="w-4 h-4 text-[#8A8F95]" />
        <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, strength…" className="flex-1 text-sm focus:outline-none" />
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b hairline">
              {['Product', 'Category', 'Price', 'Stock', 'Status', 'Flags', ''].map((h) => (
                <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className={`border-b hairline last:border-0 ${!p.active ? 'opacity-40' : ''}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={p.primary_image} alt="" className="w-10 h-10 object-cover bg-[#F4F4F0]" />
                    <div>
                      <p className="font-display font-bold uppercase leading-tight">{p.name} <span className="text-[#8A8F95]">{p.strength}</span></p>
                      <p className="font-mono2 text-[10px] text-[#8A8F95]">{p.sku} · {p.format}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider" style={{ color: catOf(p.category).color }}>{catOf(p.category).name}</span></td>
                <td className="p-3 font-mono2">{gbp(p.retail_price)}{p.sale_price ? <span className="text-[#E54D2E] ml-1">→ {gbp(p.sale_price)}</span> : ''}</td>
                <td className="p-3 font-mono2">{p.track_stock ? p.stock_quantity : '∞'}</td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-1">{STATUS_LABELS[p.status]}</span></td>
                <td className="p-3">
                  <div className="flex gap-1">
                    {p.coa_available && <FileCheck className="w-4 h-4" />}
                    {p.featured && <span className="font-mono2 text-[9px] border hairline px-1">F</span>}
                    {p.popular && <span className="font-mono2 text-[9px] border hairline px-1">P</span>}
                    {p.new_arrival && <span className="font-mono2 text-[9px] border hairline px-1">N</span>}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 hover:bg-[#F4F4F0]"><Pencil className="w-4 h-4" /></button>
                    {p.active && <button data-testid={`product-archive-${p.id}`} onClick={() => archive(p)} className="p-1.5 hover:bg-[#F4F4F0] text-[#E54D2E]"><Archive className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? `Edit — ${form.name}` : 'Add Product'} onClose={() => setEditing(null)} wide>
          <div className="flex gap-1 border-b hairline mb-5 overflow-x-auto">
            {TABS.map(([k, l]) => (
              <button key={k} data-testid={`editor-tab-${k}`} onClick={() => setTab(k)}
                className={`px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest whitespace-nowrap border-b-2 -mb-px ${tab === k ? 'border-[#0B0F12]' : 'border-transparent text-[#8A8F95]'}`}>
                {l}
              </button>
            ))}
          </div>

          {tab === 'core' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Product name *"><input data-testid="field-name" value={form.name} onChange={(e) => set('name', e.target.value)} className={inp} /></Field>
              <Field label="Alternate name"><input data-testid="field-alternate-name" value={form.alternate_name} onChange={(e) => set('alternate_name', e.target.value)} className={inp} /></Field>
              <Field label="Strength *"><input data-testid="field-strength" value={form.strength} onChange={(e) => set('strength', e.target.value)} className={inp} placeholder="e.g. 30mg" /></Field>
              <Field label="Variant"><input data-testid="field-variant" value={form.variant} onChange={(e) => set('variant', e.target.value)} className={inp} /></Field>
              <Sel label="Category" value={form.category} onChange={(v) => set('category', v)} options={CATS} testid="field-category" />
              <Sel label="Format" value={form.format} onChange={(v) => set('format', v)} options={FORMATS} testid="field-format" />
              <Field label="SKU (auto if blank)"><input data-testid="field-sku" value={form.sku} onChange={(e) => set('sku', e.target.value)} className={inp} /></Field>
              <Field label="URL slug (auto if blank)"><input data-testid="field-slug" value={form.slug} onChange={(e) => set('slug', e.target.value)} className={inp} /></Field>
              <Sel label="Status" value={form.status} onChange={(v) => set('status', v)} options={STATUSES} testid="field-status" />
              <Field label="Badge text"><input data-testid="field-badge" value={form.badge} onChange={(e) => set('badge', e.target.value)} className={inp} placeholder="e.g. Best Seller" /></Field>
              <div className="sm:col-span-2"><Field label="Internal admin note (never public)"><input data-testid="field-admin-notes" value={form.admin_notes || ''} onChange={(e) => set('admin_notes', e.target.value)} className={inp} placeholder="e.g. PRICE REVIEW REQUIRED — …" /></Field></div>
              <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Tgl label="Featured" checked={form.featured} onChange={(v) => set('featured', v)} testid="field-featured" />
                <Tgl label="Popular" checked={form.popular} onChange={(v) => set('popular', v)} testid="field-popular" />
                <Tgl label="New arrival" checked={form.new_arrival} onChange={(v) => set('new_arrival', v)} testid="field-new-arrival" />
                <Tgl label="Active (visible)" checked={form.active} onChange={(v) => set('active', v)} testid="field-active" />
              </div>
            </div>
          )}

          {tab === 'content' && (
            <div className="space-y-4">
              <Field label="Short description"><textarea data-testid="field-short-description" rows={2} value={form.short_description} onChange={(e) => set('short_description', e.target.value)} className={inp} /></Field>
              <Field label="Long description (markdown)"><textarea data-testid="field-long-description" rows={5} value={form.long_description} onChange={(e) => set('long_description', e.target.value)} className={inp} /></Field>
              <Field label="Research information (markdown)"><textarea data-testid="field-research-information" rows={5} value={form.research_information} onChange={(e) => set('research_information', e.target.value)} className={inp} /></Field>
              <Field label="Specification"><textarea data-testid="field-specification" rows={3} value={form.specification} onChange={(e) => set('specification', e.target.value)} className={inp} /></Field>
              <Field label="Composition"><input data-testid="field-composition" value={form.composition} onChange={(e) => set('composition', e.target.value)} className={inp} /></Field>
              <Field label="Storage information"><textarea data-testid="field-storage-information" rows={3} value={form.storage_information} onChange={(e) => set('storage_information', e.target.value)} className={inp} /></Field>
            </div>
          )}

          {tab === 'pricing' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Retail price (£)"><input data-testid="field-retail-price" type="number" step="0.01" value={form.retail_price} onChange={(e) => set('retail_price', e.target.value)} className={inp} /></Field>
              <Field label="Compare-at price (£)"><input data-testid="field-compare-price" type="number" step="0.01" value={form.compare_at_price ?? ''} onChange={(e) => set('compare_at_price', e.target.value)} className={inp} /></Field>
              <Field label="Sale price (£, optional)"><input data-testid="field-sale-price" type="number" step="0.01" value={form.sale_price ?? ''} onChange={(e) => set('sale_price', e.target.value)} className={inp} /></Field>
              <Field label="Stock quantity"><input data-testid="field-stock-quantity" type="number" value={form.stock_quantity} onChange={(e) => set('stock_quantity', e.target.value)} className={inp} /></Field>
              <Field label="Low stock alert threshold"><input data-testid="field-low-stock" type="number" value={form.low_stock_threshold} onChange={(e) => set('low_stock_threshold', e.target.value)} className={inp} /></Field>
              <div className="flex items-end"><Tgl label="Track stock" checked={form.track_stock} onChange={(v) => set('track_stock', v)} testid="field-track-stock" /></div>
            </div>
          )}

          {tab === 'media' && (
            <div className="space-y-4">
              <ImagePicker label="Primary image" value={form.primary_image} onChange={(v) => { set('primary_image', v); if (!form.gallery_images?.length) set('gallery_images', [v]); }} testid="field-primary-image" />
              <Field label="Gallery image URLs (one per line)">
                <textarea data-testid="field-gallery" rows={3} value={(form.gallery_images || []).join('\n')}
                  onChange={(e) => set('gallery_images', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} className={inp} />
              </Field>
            </div>
          )}

          {tab === 'docs' && (
            <div className="space-y-4">
              <Field label="Current batch number"><input data-testid="field-batch" value={form.batch_number} onChange={(e) => set('batch_number', e.target.value)} className={inp} placeholder="e.g. VQ-2026-RTD-041" /></Field>
              <Tgl label="COA available for current batch" checked={form.coa_available} onChange={(v) => set('coa_available', v)} testid="field-coa-available" />
              <FilePicker label="COA document (PDF)" value={form.coa_document} onChange={(v) => set('coa_document', v)} testid="field-coa-document" />
              <FilePicker label="SDS document" value={form.sds_document} onChange={(v) => set('sds_document', v)} testid="field-sds-document" />
              <p className="text-[11px] text-[#8A8F95]">Tip: also add the COA to the COA Library (Admin → COAs) so it is searchable by batch.</p>
            </div>
          )}

          {tab === 'variants' && (
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <p className="text-[12px] text-[#575B60] leading-relaxed max-w-lg">
                  Strength variants drive the SELECT STRENGTH dropdown on the product page. Each variant has its own price, stock, SKU, batch, COA and status.
                  The catalogue card automatically shows “FROM £min · N STRENGTHS”. Products with variants use variant-level pricing and stock.
                </p>
                <button data-testid="add-variant" onClick={() => set('variants', [...(form.variants || []), { id: `var_${Date.now().toString(36)}`, strength: '', retail_price: 0, sku: '', batch_number: '', stock_quantity: 0, status: 'available', active: true, coa_available: false, coa_document: '', image: '' }])}
                  className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#E54D2E] transition-colors whitespace-nowrap">
                  <Plus className="w-3.5 h-3.5" /> Add Variant
                </button>
              </div>
              {(form.variants || []).length === 0 && (
                <p className="border hairline bg-white p-4 text-sm text-[#8A8F95]">No variants — this product uses the single strength/price/stock from the Pricing & Stock tab.</p>
              )}
              <div className="space-y-3">
                {(form.variants || []).map((v, i) => {
                  const setV = (k, val) => set('variants', form.variants.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
                  return (
                    <div key={v.id || i} data-testid={`variant-row-${i}`} className="border hairline bg-white p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Field label="Strength *"><input data-testid={`variant-strength-${i}`} value={v.strength} onChange={(e) => setV('strength', e.target.value)} className={inp} placeholder="e.g. 40mg" /></Field>
                        <Field label="Price (£)"><input data-testid={`variant-price-${i}`} type="number" step="0.01" value={v.retail_price} onChange={(e) => setV('retail_price', e.target.value === '' ? '' : Number(e.target.value))} className={inp} /></Field>
                        <Field label="Stock"><input data-testid={`variant-stock-${i}`} type="number" min="0" value={v.stock_quantity} onChange={(e) => setV('stock_quantity', e.target.value === '' ? '' : Number(e.target.value))} className={inp} /></Field>
                        <Field label="SKU"><input data-testid={`variant-sku-${i}`} value={v.sku || ''} onChange={(e) => setV('sku', e.target.value)} className={inp} placeholder="auto" /></Field>
                        <Field label="Batch"><input data-testid={`variant-batch-${i}`} value={v.batch_number || ''} onChange={(e) => setV('batch_number', e.target.value)} className={inp} /></Field>
                        <Sel label="Status" value={v.status || 'available'} onChange={(val) => setV('status', val)} options={STATUSES} testid={`variant-status-${i}`} />
                        <div className="flex flex-col gap-1.5 justify-end">
                          <Tgl label="Active" checked={v.active !== false} onChange={(val) => setV('active', val)} testid={`variant-active-${i}`} />
                          <Tgl label="COA available" checked={!!v.coa_available} onChange={(val) => setV('coa_available', val)} testid={`variant-coa-${i}`} />
                        </div>
                        <div className="flex items-end">
                          <button data-testid={`variant-delete-${i}`} onClick={() => set('variants', form.variants.filter((_, xi) => xi !== i))}
                            className="border hairline px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest text-[#E54D2E] hover:border-[#E54D2E] w-full">Remove</button>
                        </div>
                      </div>
                      {v.coa_available && (
                        <div className="mt-3">
                          <FilePicker label="Variant COA document" value={v.coa_document || ''} onChange={(val) => setV('coa_document', val)} testid={`variant-coa-doc-${i}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'research' && (
            <div className="space-y-4">
              <p className="text-[12px] text-[#575B60]">Expandable RESEARCH OVERVIEW module on the product page. Leave blank to hide the module. Educational content only — no dosing or administration information.</p>
              {[['overview', 'Overview'], ['research_context', 'Research Context'], ['mechanism', 'Mechanism / Area of Study'], ['published_research', 'Published Research'], ['documentation', 'Documentation'], ['important', 'Important Information']].map(([k, label]) => (
                <Field key={k} label={`${label} (markdown)`}>
                  <textarea data-testid={`ro-${k}`} rows={4} value={(form.research_overview || {})[k] || ''}
                    onChange={(e) => set('research_overview', { ...(form.research_overview || {}), [k]: e.target.value })} className={inp} />
                </Field>
              ))}
              <Field label="Related journal article slugs (comma separated)">
                <input data-testid="ro-related" value={((form.research_overview || {}).related || []).join(', ')}
                  onChange={(e) => set('research_overview', { ...(form.research_overview || {}), related: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className={inp} />
              </Field>
            </div>
          )}

          {tab === 'seo' && (
            <div className="space-y-4">
              <Field label="SEO title"><input data-testid="field-seo-title" value={form.seo_title} onChange={(e) => set('seo_title', e.target.value)} className={inp} /></Field>
              <Field label="SEO description"><textarea data-testid="field-seo-description" rows={3} value={form.seo_description} onChange={(e) => set('seo_description', e.target.value)} className={inp} /></Field>
            </div>
          )}

          <div className="flex gap-2 mt-6 pt-4 border-t hairline">
            <button data-testid="product-save" onClick={save} disabled={saving}
              className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
              {saving ? 'Saving…' : 'Save product'}
            </button>
            <button onClick={() => setEditing(null)} className="border hairline px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest">Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
